(() => {
  if (customElements.get('product-lightbox')) return;

  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const SWIPE_DISTANCE_RATIO = 0.15; // fraction of viewport width to change slide
  const SWIPE_VELOCITY = 0.5;        // px/ms flick threshold
  const DISMISS_DISTANCE = 110;      // px of vertical drag to close

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.viewport = this.querySelector('.product-lightbox__viewport');
      this.track = this.querySelector('.product-lightbox__track');
      this.slides = Array.from(this.querySelectorAll('.product-lightbox__slide'));
      this.counterEl = this.querySelector('[data-lightbox-current]');
      this.prevBtn = this.querySelector('[data-lightbox-prev]');
      this.nextBtn = this.querySelector('[data-lightbox-next]');
      this.closeBtn = this.querySelector('[data-lightbox-close]');
      if (!this.dialog || !this.track || !this.slides.length) return;

      this.index = 0;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.opener = null;
      this.lastTap = { time: 0, x: 0, y: 0 };

      this.closeBtn?.addEventListener('click', () => this.close());
      this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));

      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.onClosed());
      this.dialog.addEventListener('keydown', (e) => this.onKeyDown(e));

      const vp = this.viewport;
      vp.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      vp.addEventListener('pointermove', (e) => this.onPointerMove(e));
      vp.addEventListener('pointerup', (e) => this.onPointerUp(e));
      vp.addEventListener('pointercancel', (e) => this.onPointerUp(e));
      vp.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
      vp.addEventListener('dblclick', (e) => this.onDoubleActivate(e.clientX, e.clientY));

      // Kept as a property so disconnectedCallback can remove it. A window
      // listener holding `this` would otherwise pin the whole replaced product
      // page in memory on every combined-listing colour swap.
      this.onWindowResize = () => {
        if (this.dialog.open) {
          this.resetZoom();
          this.positionTrack(false);
        }
      };
      window.addEventListener('resize', this.onWindowResize);

      this.bindGallery();
    }

    disconnectedCallback() {
      if (this.onWindowResize) {
        window.removeEventListener('resize', this.onWindowResize);
        this.onWindowResize = null;
      }
      if (this.dialog?.open) this.close();
      this.opener = null;
    }

    /* ------------------------------------------------------------------ */
    /* Gallery wiring                                                      */
    /* ------------------------------------------------------------------ */

    bindGallery() {
      const gallery = document.getElementById(this.dataset.gallery || '');
      if (!gallery) return;

      let downX = 0;
      let downY = 0;
      gallery.addEventListener('pointerdown', (e) => {
        downX = e.clientX;
        downY = e.clientY;
      }, { capture: true, passive: true });

      gallery.addEventListener('click', (e) => {
        const item = e.target instanceof Element
          ? e.target.closest('.product__media-item:not(.product__media-item--full)')
          : null;
        if (!item) return;

        const wasDrag = e.detail > 0
          && (Math.abs(e.clientX - downX) > 10 || Math.abs(e.clientY - downY) > 10);
        if (wasDrag) return;

        const mediaId = (item.dataset.mediaId || '').split('-').pop();
        const slideIndex = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
        if (slideIndex === -1) return;

        e.preventDefault();
        e.stopPropagation();
        this.open(slideIndex, item);
      }, true);
    }

    /* ------------------------------------------------------------------ */
    /* Open / close                                                        */
    /* ------------------------------------------------------------------ */

    open(index, opener = null) {
      this.opener = opener;
      this.index = clamp(index, 0, this.slides.length - 1);
      this.resetZoom();

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', ''); // very old Safari fallback
      }

      document.documentElement.classList.add('product-lightbox-open');
      this.positionTrack(false);
      this.afterSlideChange();
      this.closeBtn?.focus({ preventScroll: true });
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onClosed();
      }
    }

    onClosed() {
      document.documentElement.classList.remove('product-lightbox-open');
      this.resetZoom();
      this.track.style.transition = 'none';
      this.track.style.opacity = '';
      this.opener?.focus?.({ preventScroll: true });
      this.opener = null;
    }

    /* ------------------------------------------------------------------ */
    /* Navigation                                                          */
    /* ------------------------------------------------------------------ */

    goTo(index, animate = true) {
      const next = clamp(index, 0, this.slides.length - 1);
      if (next !== this.index) {
        this.index = next;
        this.resetZoom();
      }
      this.positionTrack(animate);
      this.afterSlideChange();
    }

    positionTrack(animate) {
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
      if (!animate) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }

    afterSlideChange() {
      if (this.counterEl) this.counterEl.textContent = String(this.index + 1);
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;

      this.slides.forEach((slide, i) => {
        const isCurrent = i === this.index;
        slide.setAttribute('aria-hidden', String(!isCurrent));
        if (Math.abs(i - this.index) <= 1) {
          const img = slide.querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        }
        if (isCurrent) this.trackImageLoading(slide);
      });
    }

    trackImageLoading(slide) {
      const img = slide.querySelector('img');
      if (!img || img.complete) {
        slide.classList.remove('is-loading');
        return;
      }
      slide.classList.add('is-loading');
      const done = () => slide.classList.remove('is-loading');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
    }

    /* ------------------------------------------------------------------ */
    /* Zoom                                                                */
    /* ------------------------------------------------------------------ */

    get currentImage() {
      return this.slides[this.index]?.querySelector('img') || null;
    }

    resetZoom() {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.applyZoom(false);
    }

    applyZoom(animate = true) {
      const img = this.currentImage;
      if (!img) return;
      img.style.transition = animate ? 'transform 0.25s ease-out' : 'none';
      img.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      this.classList.toggle('is-zoomed', this.scale > 1);
    }

    containedSize() {
      const img = this.currentImage;
      const rect = this.viewport.getBoundingClientRect();
      if (!img || !img.naturalWidth || !rect.width) return { w: rect.width, h: rect.height, rect };
      const imageRatio = img.naturalWidth / img.naturalHeight;
      const viewportRatio = rect.width / rect.height;
      return imageRatio > viewportRatio
        ? { w: rect.width, h: rect.width / imageRatio, rect }
        : { w: rect.height * imageRatio, h: rect.height, rect };
    }

    clampPan() {
      const { w, h, rect } = this.containedSize();
      const maxX = Math.max(0, (w * this.scale - rect.width) / 2);
      const maxY = Math.max(0, (h * this.scale - rect.height) / 2);
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
    }

    zoomAround(clientX, clientY, newScale, animate = false) {
      const rect = this.viewport.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const next = clamp(newScale, 1, MAX_SCALE);

      this.tx = (clientX - cx) - (next / this.scale) * (clientX - cx - this.tx);
      this.ty = (clientY - cy) - (next / this.scale) * (clientY - cy - this.ty);
      this.scale = next;
      if (this.scale === 1) { this.tx = 0; this.ty = 0; }
      this.clampPan();
      this.applyZoom(animate);
    }

    onDoubleActivate(clientX, clientY) {
      this.zoomAround(clientX, clientY, this.scale > 1 ? 1 : DOUBLE_TAP_SCALE, true);
    }

    onWheel(e) {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0022);
      this.zoomAround(e.clientX, e.clientY, this.scale * factor, false);
    }

    /* ------------------------------------------------------------------ */
    /* Pointer gestures: swipe, pan, pinch, dismiss                        */
    /* ------------------------------------------------------------------ */

    onPointerDown(e) {
      this.viewport.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 1) {
        this.gesture = {
          startX: e.clientX,
          startY: e.clientY,
          startTx: this.tx,
          startTy: this.ty,
          startTime: performance.now(),
          mode: null, // 'swipe' | 'pan' | 'dismiss' | 'pinch'
        };
        this.track.style.transition = 'none';
      } else if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.gesture = {
          mode: 'pinch',
          startDistance: Math.hypot(a.x - b.x, a.y - b.y),
          startScale: this.scale,
        };
      }
    }

    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId) || !this.gesture) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.gesture.mode === 'pinch' && this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (!this.gesture.startDistance) return;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        this.zoomAround(midX, midY, this.gesture.startScale * (distance / this.gesture.startDistance));
        return;
      }

      const dx = e.clientX - this.gesture.startX;
      const dy = e.clientY - this.gesture.startY;

      if (!this.gesture.mode) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (this.scale > 1) this.gesture.mode = 'pan';
        else this.gesture.mode = Math.abs(dy) > Math.abs(dx) ? 'dismiss' : 'swipe';
      }

      if (this.gesture.mode === 'pan') {
        this.tx = this.gesture.startTx + dx;
        this.ty = this.gesture.startTy + dy;
        this.clampPan();
        this.applyZoom(false);
      } else if (this.gesture.mode === 'swipe') {
        const width = this.viewport.clientWidth || 1;
        let offset = dx;
        // Rubber-band at the ends.
        const atStart = this.index === 0 && dx > 0;
        const atEnd = this.index === this.slides.length - 1 && dx < 0;
        if (atStart || atEnd) offset = dx * 0.3;
        this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${offset}px), 0, 0)`;
        void width;
      } else if (this.gesture.mode === 'dismiss') {
        const pull = Math.max(0, dy);
        this.track.style.transform = `translate3d(${-this.index * 100}%, ${pull}px, 0)`;
        this.track.style.opacity = String(clamp(1 - pull / 400, 0.4, 1));
      }
    }

    onPointerUp(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.delete(e.pointerId);
      if (this.pointers.size > 0) return; // still pinching with the other finger
      const gesture = this.gesture;
      this.gesture = null;
      if (!gesture) return;

      if (gesture.mode === 'pinch') {
        if (this.scale <= 1.02) this.resetZoom();
        return;
      }

      const dx = e.clientX - (gesture.startX ?? e.clientX);
      const dy = e.clientY - (gesture.startY ?? e.clientY);
      const elapsed = Math.max(1, performance.now() - (gesture.startTime ?? performance.now()));

      if (gesture.mode === 'swipe') {
        const width = this.viewport.clientWidth || 1;
        const velocity = Math.abs(dx) / elapsed;
        if (Math.abs(dx) > width * SWIPE_DISTANCE_RATIO || velocity > SWIPE_VELOCITY) {
          this.goTo(dx < 0 ? this.index + 1 : this.index - 1);
        } else {
          this.goTo(this.index);
        }
        return;
      }

      if (gesture.mode === 'dismiss') {
        if (dy > DISMISS_DISTANCE) {
          this.close();
        } else {
          this.track.style.opacity = '';
          this.goTo(this.index);
        }
        return;
      }

      if (!gesture.mode && e.pointerType === 'touch') {
        const now = performance.now();
        const { time, x, y } = this.lastTap;
        if (now - time < DOUBLE_TAP_MS && Math.hypot(e.clientX - x, e.clientY - y) < 40) {
          this.lastTap = { time: 0, x: 0, y: 0 };
          this.onDoubleActivate(e.clientX, e.clientY);
        } else {
          this.lastTap = { time: now, x: e.clientX, y: e.clientY };
        }
      }
    }
  }

  customElements.define('product-lightbox', ProductLightbox);
})();
